import { Fab, Grid } from '@mui/material';
import { Box } from '@mui/system';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import LineSvg from '../Components/LineSvg';
import CircleSvg from '../Components/CircleSvg';
import DottedCircleSvg from '../Components/DottedCircleSvg';
import LaunchIcon from '@mui/icons-material/Launch';
import '../Style/ImportantConcepts.scss';

const ImportantConcepts = () => {
	const goBack = () => {
		window.history.go(-1);
		return false;
	};

	return (
		<Grid container direction='column' className='wrapper-importantConcepts'>
			<Box sx={{ flexGrow: 1 }} className='importantConcepts'>
				<Grid
					container
					justifyContent='center'
				// spacing={{ xs: 2, sm: 2, md: 10 }}
				>
					<Grid
						xs={12}
						sm={10}
						md={6}
						lg={5}
						item
						paddingLeft={{ xs: '0px', sm: '0px', md: '2rem' }}
						paddingRight={{ xs: '0px', sm: '0px', md: '2rem' }}
					>
						<Box flexDirection='row' display='flex'>
							<Fab size='small' className='fabButton' onClick={goBack}>
								<ArrowBackRoundedIcon />
							</Fab>
							<span
								style={{
									display: 'flex',
									alignItems: 'center',
									fontSize: '15px',
									marginLeft: '1rem'
								}}
							>
								<b>Back</b>
							</span>
						</Box>
						<Box className='importantConceptsBox importantConceptsBox2'>
							<div
								className='circleImportantConcepts";
'
							>
								<Box
									style={{
										rotate: '180deg',
										position: 'absolute',
										right: 0,
										top: '3rem'
									}}
								>
									<CircleSvg />
								</Box>
							</div>
							<div className='lineSvg'>
								<LineSvg />
							</div>
							<Box className='titleWrapper'>
								<h1>Methodology</h1>
							</Box>
							<h3>
								Samples without quantifiable residues or quantifiable pesticide
								residues were not found
							</h3>
							<p>
							Either term is used to describe samples where the analytes were not present in concentrations at or exceeding the limit of quantification (LOQ).
							</p>
							<h3>
								Samples with quantified residues within the legal limits (below
								or at the MRL)
							</h3>
							<p>
								These samples contained quantified residues (i.e. above the LOQ)
								of one or several pesticides but in concentrations below or at
								the MRL.
							</p>
							<h3>
								Samples with quantified residues exceeding the legal limit
								(above the MRL)
							</h3>
							<p>
								These samples contained pesticide residue concentrations in mg/kg reported numerically higher than the maximum residue level.
							</p>
							<h3>Non-compliant samples</h3>
							<p>
								Samples with at least one quantified residue where, after considering measurement uncertainty,
								both tails of the dispersion are above the maximum residue level. The concept of measurement
								uncertainties and its impact on the decision of non-compliance is described in Figure 1 of EFSA’s 2017
								guidance document (<a className='readMore'
								href="https://www.efsa.europa.eu/en/efsajournal/pub/5285"
								target='_blank'
								rel='noreferrer'
								>doi: 10.2903/j.efsa.2018.5285
									<LaunchIcon
											sx={{
												fontSize: 'small',
												color: '#22294D',
												position: 'relative',
												top: '3px'
											}}
										/>
									</a>) on reporting data on pesticide residues.
									It is required for official controls that the uncertainty of the analytical
									measurement is considered before legal or administrative sanctions are imposed
									on food business operators for infringement of the MRL legislation.
							</p>
							<h3>
							Samples with multiple residues
							</h3>
							<p style={{ paddingBottom: '60px' }}>
							The presence of multiple pesticide residues in the same sample does not constitute non-compliance with MRL legislation as long as MRLs are not exceeded by individual residues. However, products with multiple residues should be assessed carefully by the national authorities (for example, to consider whether combinations of pesticides are being used deliberately to circumvent MRL limits on single substances).
							</p>
							<Box
								className='bottom-circle'
								display={{ sm: 'none', md: 'block', lg: 'block' }}
								sx={{
									position: 'absolute',
									bottom: 0
								}}
							>
								<DottedCircleSvg />
							</Box>
						</Box>
					</Grid>
					<Grid
						xs={12}
						sm={10}
						md={6}
						lg={5}
						item
						sx={{ display: 'flex' }}
						paddingLeft={{ xs: '0px', sm: '0px', md: '2rem' }}
						paddingRight={{ xs: '0px', sm: '0px', md: '2rem' }}
						marginLeft={{ xs: '0px', sm: '0px', md: '5rem' }}
					>
						<Box
							flexDirection='column'
							className='importantConceptsBox importantConceptsBox1'
							display='flex'
						>
							{' '}
							<div className='lineSvg'>
								<LineSvg />
							</div>
							<Box className='titleWrapper'>
								<h1>Terminology</h1>
							</Box>
							<Box>
								<h3>Pesticides</h3>
								<p>
								In the context of this report, the term 'pesticides' is used as a synonym for plant protection products (PPP). They consist of or contain active and other substances added to plants and/or their products to ensure, among others, their protection against harmful organisms, influence their life processes (e.g. growth regulators), and/or destroy or prevent growth of undesired plants or parts of them in fields, etc.
								</p>
								<h3>Pesticide residue</h3>
								<p>
									A 'pesticide residue' consists of or contains measurable
									amounts of an active substance and/or related metabolites and
									degradation products that can be found on harvested crops or
									in foods of animal origin.
								</p>
								<h3>Residue definition (RD)</h3>
								<p>
								In this report, the term 'residue definition (RD)' refers to all substances generated from the presence of a pesticide in a crop, food or feed. A residue definition may be simple (i.e. one substance only) or complex (i.e. more than one substance). Considering that the substances used for the estimation of the dietary exposure to a pesticide residue may not coincide with those used for the setting and enforcing of maximum residue limits (MRLs), different residue definitions may be implemented at EU level for risk assessment and enforcement purposes. In this report dealing with pesticide monitoring, the RD refers to the enforcement RD.
								</p>
								<h3>Domestic samples</h3>
								<p>
								Domestic samples are samples of commodities grown in the country reporting the results of its analysis.
								</p>
								<h3>Third countries</h3>
								<p>
								From the EU perspective, a third country is all the countries world-wide, except those EFTA countries considered second countries and those in the EU that are considered first countries. In other words, is the different commercial relations among the EU and the rest of countries (as an example: USA is a third country as it is India).
								</p>
								<h3>Composite food products</h3>
								<p>
								In this report, the term 'composite food products' refers to food made of more than one product, either plant-based raw agricultural commodities or animal commodities. The applicable MRL needs to be recalculated depending on the ingredients and proportions of these.
								</p>
								<h3>Maximum residue level (MRL)</h3>
								<p>
								The upper legal level of a concentration for a pesticide residue in or on food, set in accordance with Annex I of Regulation (EC) 396/2005, based on good agricultural practice and the lowest consumer exposure necessary to protect vulnerable consumers.
								</p>
								<h3>EU multiannual control programme</h3>
								<p>

The EU MACP gathers data on the occurrence and possible MRL exceedances of pesticide residues in food placed on the European market that are statistically representative enough to estimate the exposure of EU consumers to these residues. The EU MACP is defined under Regulation (EU) No 2021/601 and Regulation (EU) No 2020/2041, which specify combinations of pesticides (190 pesticides) and a minimum number of samples for 12 food products to be analysed by EU Member States and the United Kingdom in respect of Northern Ireland, depending on the country population size. Chapter one of the report gives a snapshot of the respective situation in 2023.
								</p>
								<h3>Multiannual national control programmes</h3>
								<p>
								The MANCP are mainly risk-based sampling programmes that are complementary to the EU MACP. They focus on products likely to contain pesticide residues or for which there were MRL infringements in previous monitoring programmes. These programmes are not designed to provide statistically representative results for residues expected in food placed on the European market. The reporting countries define the priorities for their national control programmes considering the importance of food products for trade or national diets, the products with high residue prevalence or non-compliance rates in previous years, and the pesticide usage patterns. The number of samples and/or pesticides analysed by the participating countries is determined by the capacities of national control laboratories and available budget resources. Because they reflect the specific needs and priorities of the reporting countries, the results of national control programmes are not directly comparable. Chapter two of the report gives a snapshot of the respective situation in 2023.
								</p>
								<b>Read more</b>
								<p class='readMore'>
									<a
										href='https://ec.europa.eu/food/plants/pesticides_en'
										target='_blank'
										rel='noreferrer'
									>
										Pesticides(europa.eu)
										<LaunchIcon
											sx={{
												fontSize: 'small',
												color: '#22294D',
												position: 'relative',
												top: '3px'
											}}
										/>
									</a>
								</p>
							</Box>
						</Box>
					</Grid>
				</Grid>
			</Box>
		</Grid>
	);
};

export default ImportantConcepts;
